import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _asyncToGenerator from '@babel/runtime/helpers/asyncToGenerator';
import _createClass from '@babel/runtime/helpers/createClass';
import _classCallCheck from '@babel/runtime/helpers/classCallCheck';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _regeneratorRuntime from '@babel/runtime/regenerator';
import axios from 'axios';
import { omit } from 'lodash-es';
import ArgumentPath from './argumentPath.js';
var _excluded = ["headers", "method", "url", "data", "params"],
  _excluded2 = ["headers", "method", "url", "data", "params", "accessToken"],
  _excluded3 = ["headers", "method", "url", "data", "params"];
function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}

/**
 * API Client
 *
 * API base class for generating individual API clients for each API with their
 * own methods.
 *
 * It has support for replacing parameters in paths automatically, interceptors,
 * headers and timeouts.
 */
var ApiClient = /*#__PURE__*/_createClass(
/**
 * @param {boolean} isLocal
 * @param {string} baseUrl
 * @param {object} headers
 * @param {object} interceptors
 * @param {object} interceptors.request
 * @param {func} interceptors.request.onFulfilled
 * @param {func} interceptors.request.onRejected
 * @param {object} interceptors.response
 * @param {func} interceptors.response.onFulfilled
 * @param {func} interceptors.response.onRejected
 * @param {number} timeout
 */
function ApiClient(isLocal, _ref) {
  var _this = this,
    _this$interceptors,
    _this$interceptors2,
    _this$interceptors3,
    _this$interceptors4;
  var baseUrl = _ref.baseUrl,
    _headers = _ref.headers,
    interceptors = _ref.interceptors,
    timeout = _ref.timeout;
  _classCallCheck(this, ApiClient);
  _defineProperty(this, "isLocal", null);
  _defineProperty(this, "baseUrl", null);
  _defineProperty(this, "headers", null);
  _defineProperty(this, "interceptors", null);
  _defineProperty(this, "instance", null);
  // Time in ms the request waits for a server response
  _defineProperty(this, "timeout", 3000);
  /**
   * Default request function
   *
   * Auto-replaces parameters in `url` (e.g. `{id}`) with the given properties
   * in `data` via the `ArgumentPath` class.
   *
   * @param {object} headers
   * @param {string} method
   * @param {string} url
   * @param {object} data
   * @param {object} params
   * @param rest
   * @returns {Promise<void>}
   */
  _defineProperty(this, "request", /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref2) {
      var headers, _ref2$method, method, url, data, params, rest, argumentPath;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            headers = _ref2.headers, _ref2$method = _ref2.method, method = _ref2$method === void 0 ? 'get' : _ref2$method, url = _ref2.url, data = _ref2.data, params = _ref2.params, rest = _objectWithoutProperties(_ref2, _excluded);
            argumentPath = new ArgumentPath({
              path: url,
              args: data,
              isLocal: _this.isLocal
            });
            return _context.abrupt("return", _this.instance.request(_objectSpread({
              method: method,
              headers: headers,
              // Use the processed url and data object from `argumentPath` if there are
              // parameters in the URL
              url: argumentPath.hasParameters ? argumentPath.processed : url,
              data: argumentPath.hasParameters ? argumentPath.argsWithoutParameters : data,
              params: params
            }, rest)));
          case 3:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function (_x) {
      return _ref3.apply(this, arguments);
    };
  }());
  /**
   * Request function with an `accessToken` requirement.
   *
   * @param {object} headers
   * @param {string} method
   * @param {string} url
   * @param {object} data
   * @param {object} params
   * @param {string} accessToken
   * @param rest
   * @returns {Promise<void>}
   */
  _defineProperty(this, "requestWithAccessToken", /*#__PURE__*/function () {
    var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(_ref4) {
      var headers, method, url, data, params, accessToken, rest;
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            headers = _ref4.headers, method = _ref4.method, url = _ref4.url, data = _ref4.data, params = _ref4.params, accessToken = _ref4.accessToken, rest = _objectWithoutProperties(_ref4, _excluded2);
            if (accessToken) {
              _context2.next = 3;
              break;
            }
            throw new Error('`accessToken` is required.');
          case 3:
            return _context2.abrupt("return", _this.request(_objectSpread({
              headers: _objectSpread({
                Authorization: "Bearer ".concat(accessToken)
              }, headers),
              method: method,
              url: url,
              data: data,
              params: params
            }, rest)));
          case 4:
          case "end":
            return _context2.stop();
        }
      }, _callee2);
    }));
    return function (_x2) {
      return _ref5.apply(this, arguments);
    };
  }());
  /**
   * Request function with a `refreshToken` requirement.
   *
   * @param {object} headers
   * @param {string} method
   * @param {string} url
   * @param {object} data
   * @param {string} data.refreshToken
   * @param {object} params
   * @param {string} params.refreshToken
   * @param rest
   * @returns {Promise<void>}
   */
  _defineProperty(this, "requestWithRefreshToken", /*#__PURE__*/function () {
    var _ref7 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(_ref6) {
      var method, url, data, params, rest, refreshTokenAsParam, refreshTokenAsData;
      return _regeneratorRuntime.wrap(function _callee3$(_context3) {
        while (1) switch (_context3.prev = _context3.next) {
          case 0:
            _ref6.headers, method = _ref6.method, url = _ref6.url, data = _ref6.data, params = _ref6.params, rest = _objectWithoutProperties(_ref6, _excluded3);
            refreshTokenAsParam = params === null || params === void 0 ? void 0 : params.refreshToken;
            refreshTokenAsData = data === null || data === void 0 ? void 0 : data.refreshToken;
            if (!(!refreshTokenAsParam && !refreshTokenAsData)) {
              _context3.next = 5;
              break;
            }
            throw new Error('`refreshToken` is required as param or data.');
          case 5:
            return _context3.abrupt("return", _this.request(_objectSpread({
              method: method,
              url: url,
              data: _objectSpread({
                refresh_token: refreshTokenAsData
              }, omit(data, 'refreshToken')),
              params: _objectSpread({
                refresh_token: refreshTokenAsParam
              }, omit(params, 'refreshToken'))
            }, rest)));
          case 6:
          case "end":
            return _context3.stop();
        }
      }, _callee3);
    }));
    return function (_x3) {
      return _ref7.apply(this, arguments);
    };
  }());
  if (!baseUrl || typeof baseUrl !== 'string') {
    throw new Error('ApiClient must be given a valid `baseUrl` string.');
  }
  this.isLocal = isLocal;
  this.baseUrl = baseUrl;
  this.headers = _headers || this.headers;
  this.interceptors = interceptors || this.interceptors;
  this.timeout = timeout || this.timeout;

  // Create & setup axios instance
  this.instance = axios.create({
    baseURL: this.baseUrl,
    headers: this.headers,
    timeout: this.timeout
  });

  // Apply interceptors
  this.instance.interceptors.request.use((_this$interceptors = this.interceptors) === null || _this$interceptors === void 0 || (_this$interceptors = _this$interceptors.request) === null || _this$interceptors === void 0 ? void 0 : _this$interceptors.onFulfilled, (_this$interceptors2 = this.interceptors) === null || _this$interceptors2 === void 0 || (_this$interceptors2 = _this$interceptors2.request) === null || _this$interceptors2 === void 0 ? void 0 : _this$interceptors2.onRejected);
  this.instance.interceptors.response.use((_this$interceptors3 = this.interceptors) === null || _this$interceptors3 === void 0 || (_this$interceptors3 = _this$interceptors3.response) === null || _this$interceptors3 === void 0 ? void 0 : _this$interceptors3.onFulfilled, (_this$interceptors4 = this.interceptors) === null || _this$interceptors4 === void 0 || (_this$interceptors4 = _this$interceptors4.response) === null || _this$interceptors4 === void 0 ? void 0 : _this$interceptors4.onRejected);
});
export { ApiClient as default };